<template>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :nudge-right="40"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-model="dateRangeText"
          :label="labelLocal"
          prepend-icon="mdi-calendar"
          readonly
          dense
          v-bind="attrs"
          v-on="on"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="dateRangeInternal"
        :range="range"
        :selected-items-text="selectedItemsText"
      >
        <div>
          <v-row dense style="max-width: 300px;">
            <v-col
              v-for="(button, index) in buttonsLocal"
              :key="index"
              cols="6" 
              class="d-flex justify-center"
            >
              <v-btn
                color="primary"
                style="width:140px;"
                small
                @click="setDateRange(button.code)"
              >
                {{ button.label }}
              </v-btn>
            </v-col>
          </v-row>
          <v-spacer></v-spacer>
          <v-row justify="end" class="mt-2 pa-2">
            <v-btn text color="primary" @click="reset">
                {{ $_t('Reset') }}
            </v-btn>
            <v-btn text color="primary" @click="apply">
                {{ $_t('Filter') }} 
            </v-btn>
          </v-row>
        </div>
      </v-date-picker>
    </v-menu>
  </template>
  
  <script>
  import moment from 'moment';
  import useDateHelpers from '@helpers/useDateHelpers';
  import { format } from 'date-fns'

  const { useHumanDate } = useDateHelpers()
  export default {
    name: 'DatePicker',
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      range: {
        type: Boolean,
        default: true, 
        },
      label: {
        type: String,
        default: 'Select Date(s)',
      },
      buttons: {
        type: Array,
        default: () => [
          { code: 'today', label: 'Today' },
          { code: 'tomorrow', label: 'Tomorrow' },
          { code: 'yesterday', label: 'Yesterday' },
          { code: 'last-3-days', label: 'Last 3 days' },
          { code: 'last-week', label: 'Last Week' },
          { code: 'this-week', label: 'This Week' },
          { code: 'next-week', label: 'Next Week' },
          { code: 'last-2-weeks', label: 'Last 2 Weeks' },
        ],
      },
    },
    data() {
      return {
        menu: false,
        dateRangeInternal: this.value,
        dateRangeText: this.formatRangeText(this.value),
      };
    },
    computed: {
        labelLocal() {
            return this.$_t(this.label)
        },
        buttonsLocal() {
            return this.buttons.map((button) => ({
                ...button,
                label: this.$_t(button.label), 
            }));
        },
        selectedItemsText (datePickerContext) {
          return this.formatRangeTextTitle(datePickerContext.dateRangeInternal)
        }
    },
    watch: {
      value(newValue) {
        this.dateRangeInternal = newValue
        this.dateRangeText = this.formatRangeText(newValue)
      },
    },
    mounted () {
      this.initDatePicker()
    },
    methods: {
      initDatePicker() {
        const cachedValue = sessionStorage.getItem('gridDateRange')
        const defaultValue = [
            moment().subtract(2, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
            moment().format('YYYY-MM-DD')
        ]
        this.dateRangeInternal === cachedValue || defaultValue
      },
      formatRangeText(range) {
        if (Array.isArray(range) && range.length === 2) {
            if (range[0] === range[1]){
                return useHumanDate(range[0])
            } else {
                return `${useHumanDate(range[0])} - ${useHumanDate(range[1])}`
            }
        }
        return ''
      },
      formatRangeTextTitle(range) {
        const dateFormat = 'MMM, dd'
        if (Array.isArray(range) && range.length === 2) {
          if (range[0] === range[1]){
            return format(new Date(range[0]), dateFormat)
          } else {
            return `${format(new Date(range[0]), dateFormat)} - ${format(new Date(range[1]), dateFormat)}`
          }
        }
        return ''
      },
      setDateRange(code) {
        let fromDate = moment()
        let toDate = moment()
  
        switch (code) {
            case 'today':
            break
            case 'tomorrow':
            fromDate = moment().add(1, 'days')
            toDate = moment().add(1, 'days')
            break
            case 'yesterday':
            fromDate = moment().subtract(1, 'days')
            toDate = moment().subtract(1, 'days')
            break
            case 'last-3-days':
            fromDate = moment().subtract(3, 'days')
            break
            case 'last-week':
            fromDate = moment().subtract(1, 'weeks').startOf('isoWeek')
            toDate = moment().subtract(1, 'weeks').endOf('isoWeek')
            break
            case 'this-week':
            fromDate = moment().startOf('isoWeek')
            toDate = moment().endOf('isoWeek')
            break
            case 'next-week':
            fromDate = moment().add(1, 'weeks').startOf('isoWeek')
            toDate = moment().add(1, 'weeks').endOf('isoWeek')
            break
            case 'last-2-weeks':
            fromDate = moment().subtract(2, 'weeks').startOf('isoWeek')
            toDate = moment().subtract(1, 'weeks').endOf('isoWeek')
            break
            default:
            this.$_notify.error(this.$_t('Invalid range type'))
            break
            
        }
  
        this.dateRangeInternal = [
          moment(fromDate).format('YYYY-MM-DD'),
          moment(toDate).format('YYYY-MM-DD'),
        ];
        sessionStorage.setItem('gridDateRange', JSON.stringify(this.dateRangeInternal))
        this.dateRangeText = this.formatRangeText(this.dateRangeInternal)
      },
      reset() {
        this.dateRangeInternal = [
            moment().subtract(2, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
            moment().subtract(2, 'weeks').endOf('isoWeek').format('YYYY-MM-DD')
        ]
        sessionStorage.setItem('gridDateRange', JSON.stringify(this.dateRangeInternal))
        this.dateRangeText = this.formatRangeText(this.dateRangeInternal)
        this.$emit('input', this.dateRangeInternal)
      },
      apply() {
        this.$emit('input', this.dateRangeInternal)
        sessionStorage.setItem('gridDateRange', JSON.stringify(this.dateRangeInternal))
        this.menu = false
      },
    },
  };
  </script>
